import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import api from "../api/client";

// Grand voilier au coucher du soleil - Époque des grandes découvertes
const BG_IMAGE = "https://images.unsplash.com/photo-1601472398529-220355fc39fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHx0YWxsJTIwc2hpcCUyMGdhbGxlb24lMjBzYWlsaW5nJTIwc3Vuc2V0JTIwZXBpYyUyMGRyYW1hdGljJTIwb2NlYW4lMjBhZHZlbnR1cmV8ZW58MHx8fHwxNzcyMDI0MzYxfDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "" });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
      } else {
        await register({ ...form, role: "student" });
      }
      navigate("/dashboard");
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: err.response?.data?.detail || "Une erreur est survenue",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: err.response?.data?.detail || "Une erreur est survenue",
      });
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotPassword(false);
    setForgotEmail("");
    setForgotSent(false);
  };

  return (
    <div className="login-page-equipage min-h-screen flex" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #060912 100%)" }}>
      {/* Left Panel - Background Image */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img 
            src={BG_IMAGE} 
            alt="Real Estate" 
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-slate-900/50" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-20">
            <img src="/logo-boussole.png" alt="Odys'Mentoring" className="w-14 h-14 drop-shadow-2xl" />
            <span className="font-bold text-2xl drop-shadow-lg" style={{ fontFamily: "var(--serif)" }}>
              <span className="text-white">Odys'</span><span style={{ color: "#D4A857" }}>Mentoring</span>
            </span>
          </div>

          <div className="space-y-6">
            <h1 className="text-5xl font-bold leading-tight drop-shadow-lg" style={{ fontFamily: "var(--serif)" }}>
              <span className="text-white">Votre parcours</span><br />
              <span style={{ color: "#D4A857" }}>immobilier</span><br />
              <span className="text-white">commence ici</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed max-w-md drop-shadow">
              Plateforme de coaching immobilier premium. Suivez votre progression, échangez avec votre coach et atteignez vos objectifs d'investissement.
            </p>
          </div>
        </div>

        <div className="relative z-10 space-y-4">
          {[
            { icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z", text: "Suivi personnalisé de votre progression", color: "cyan" },
            { icon: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z", text: "Messagerie directe avec votre coach", color: "emerald" },
            { icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1", text: "Connexion avec Odysboard", color: "amber" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4 group animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border backdrop-blur-sm transition-all duration-300 ${
                item.color === "cyan" ? "bg-cyan-500/20 border-cyan-500/40 group-hover:border-cyan-400" :
                item.color === "emerald" ? "bg-emerald-500/20 border-emerald-500/40 group-hover:border-emerald-400" :
                "bg-amber-500/20 border-amber-500/40 group-hover:border-amber-400"
              }`}>
                <svg className={`w-5 h-5 ${
                  item.color === "cyan" ? "text-cyan-400" :
                  item.color === "emerald" ? "text-emerald-400" : "text-amber-400"
                }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
              </div>
              <span className="text-slate-200 text-sm font-medium drop-shadow">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel - Form (Dark + Serif + Gold) */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #060912 100%)" }}>
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="text-center mb-8 lg:hidden">
            <div className="flex items-center justify-center gap-2 mb-6">
              <img src="/logo-boussole.png" alt="Odys'Mentoring" className="w-12 h-12" />
              <span className="font-bold text-xl" style={{ fontFamily: "var(--serif)" }}>
                <span className="text-white">Odys'</span><span style={{ color: "#D4A857" }}>Mentoring</span>
              </span>
            </div>
          </div>

          <div className="equipage-card rounded-3xl p-8 sm:p-10">
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: "var(--serif)" }}>
                {isLogin ? "Bon retour !" : "Créer un compte"}
              </h2>
              <p className="text-sm" style={{ color: "rgba(226, 232, 240, 0.65)" }}>
                {isLogin ? "Connectez-vous à votre espace coaching" : "Rejoignez la plateforme Odys'Mentoring"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="equipage-label">Prénom</label>
                    <input
                      data-testid="first-name-input"
                      type="text" required
                      className="equipage-input"
                      placeholder="Jean"
                      value={form.first_name}
                      onChange={e => setForm({ ...form, first_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="equipage-label">Nom</label>
                    <input
                      data-testid="last-name-input"
                      type="text" required
                      className="equipage-input"
                      placeholder="Dupont"
                      value={form.last_name}
                      onChange={e => setForm({ ...form, last_name: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="equipage-label">Email</label>
                <input
                  data-testid="email-input"
                  type="email" required
                  className="equipage-input"
                  placeholder="vous@exemple.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <label className="equipage-label" style={{ marginBottom: 0 }}>Mot de passe</label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-xs font-medium transition-colors"
                      style={{ color: "#D4A857" }}
                      onMouseOver={(e) => e.currentTarget.style.color = "#E8C175"}
                      onMouseOut={(e) => e.currentTarget.style.color = "#D4A857"}
                    >
                      Mot de passe oublié ?
                    </button>
                  )}
                </div>
                <input
                  data-testid="password-input"
                  type="password" required
                  className="equipage-input"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                />
              </div>

              <button
                data-testid="submit-btn"
                type="submit" disabled={loading}
                className="equipage-btn-gold w-full mt-4"
              >
                {loading ? "Chargement..." : isLogin ? "Se connecter" : "Créer mon compte"}
              </button>
            </form>

            {isLogin && (
              <div className="mt-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px" style={{ background: "rgba(148, 163, 184, 0.2)" }} />
                  <span className="text-xs uppercase tracking-wider" style={{ color: "rgba(226, 232, 240, 0.45)" }}>ou</span>
                  <div className="flex-1 h-px" style={{ background: "rgba(148, 163, 184, 0.2)" }} />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    const redirectUrl = window.location.origin + "/dashboard";
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="w-full flex items-center justify-center gap-3 py-3 rounded-lg font-medium transition-all"
                  style={{
                    background: "#ffffff",
                    color: "#1a1a2e",
                    border: "1px solid rgba(148, 163, 184, 0.15)",
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = "#ffffff"; }}
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="text-sm">Se connecter avec Google</span>
                </button>
                <p className="text-xs text-center mt-3" style={{ color: "rgba(148, 163, 184, 0.55)" }}>
                  Utilise l&apos;email de ton compte Odys'Mentoring
                </p>
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm" style={{ color: "rgba(226, 232, 240, 0.65)" }}>
                {isLogin ? "Pas encore de compte ?" : "Déjà un compte ?"}
                <button
                  data-testid="toggle-auth-btn"
                  onClick={() => setIsLogin(!isLogin)}
                  className="ml-2 font-semibold transition-colors"
                  style={{ color: "#D4A857" }}
                  onMouseOver={(e) => e.currentTarget.style.color = "#E8C175"}
                  onMouseOut={(e) => e.currentTarget.style.color = "#D4A857"}
                >
                  {isLogin ? "S'inscrire" : "Se connecter"}
                </button>
              </p>
            </div>

            <div className="mt-6 pt-5 text-center" style={{ borderTop: "1px solid rgba(148, 163, 184, 0.15)" }}>
              <a
                href="/cgu.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs transition-colors"
                style={{ color: "rgba(148, 163, 184, 0.5)" }}
                onMouseOver={(e) => e.currentTarget.style.color = "#D4A857"}
                onMouseOut={(e) => e.currentTarget.style.color = "rgba(148, 163, 184, 0.5)"}
              >
                Conditions Générales d'Utilisation
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Mot de passe oublié (Dark + Gold) */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" style={{ backdropFilter: "blur(4px)" }}>
          <div className="equipage-card rounded-3xl p-8 max-w-md w-full animate-fade-in-up">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: "var(--serif)" }}>Mot de passe oublié</h3>
              <button
                onClick={closeForgotModal}
                className="transition-colors"
                style={{ color: "rgba(148, 163, 184, 0.6)" }}
                onMouseOver={(e) => e.currentTarget.style.color = "#FFFFFF"}
                onMouseOut={(e) => e.currentTarget.style.color = "rgba(148, 163, 184, 0.6)"}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {forgotSent ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: "rgba(212, 168, 87, 0.18)", border: "1px solid rgba(212, 168, 87, 0.4)" }}>
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: "#D4A857" }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "var(--serif)" }}>Email envoyé !</h4>
                <p className="text-sm mb-6" style={{ color: "rgba(226, 232, 240, 0.65)" }}>
                  Si cette adresse existe dans notre système, tu recevras un email avec les instructions pour réinitialiser ton mot de passe.
                </p>
                <button
                  onClick={closeForgotModal}
                  className="equipage-btn-gold w-full"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm mb-6" style={{ color: "rgba(226, 232, 240, 0.65)" }}>
                  Entre ton adresse email et nous t'enverrons un lien pour réinitialiser ton mot de passe.
                </p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="equipage-label">Email</label>
                    <input
                      type="email"
                      required
                      className="equipage-input"
                      placeholder="vous@exemple.com"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="equipage-btn-gold w-full"
                  >
                    {forgotLoading ? "Envoi en cours..." : "Envoyer le lien"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
